import TelemetryDeck from '@telemetrydeck/sdk'

import { edition } from '@/lib/edition'
import { isAndroid, isIOS } from '@/lib/platform'
import { generateUuid } from '@/lib/uuid'

/**
 * TelemetryDeck client wrapper — single source of truth for analytics
 * signals fired from the patient app.
 *
 * Why this layer:
 *   - Guarantees a no-op when `VITE_TELEMETRYDECK_APP_ID` is blank so dev
 *     builds (and the therapist web build, where the env is not set) never
 *     reach the production app dashboard.
 *   - Attaches a "default payload" (platform, app version, locale,
 *     entitlement status, session id) to every signal so each call site
 *     only worries about the event-specific properties.
 *
 * Privacy:
 *   - TelemetryDeck SHA-256s `clientUser` before sending. We feed it the
 *     eternal `patient_id` which is already an opaque non-PII token.
 *   - No patient names, exercise names, or item words ever go through this
 *     module — see telemetry-events.ts for the curated schema.
 *
 * See plan: TelemetryDeck integration — patient app.
 */

/**
 * Snapshot the runtime exposes for the default payload. Passed in at init
 * time so this module doesn't import the Pinia store directly (Pinia
 * stores are not safe to instantiate at module-load time, before the app
 * is mounted).
 */
export interface TelemetryDefaultsProvider {
  /** Locale code, e.g. `'de'` / `'en'`. Read from `i18n.global.locale`. */
  locale(): string
  /**
   * Entitlement status; `'unknown'` while the store hydrates. Patient-only —
   * omit on the therapist build (entitlement is a consumer concept).
   */
  entitlementStatus?(): string
  /**
   * Days remaining in the trial; null if not currently in trial. Patient-only
   * — omit on the therapist build.
   */
  trialDaysRemaining?(): number | null
  /** Build role label (`'patient'` / `'therapist'`); rides every signal. */
  role?(): string
  /**
   * Opaque practice id for per-practice segmentation. Therapist-only — omit on
   * the patient build. Read lazily so signals fired before the embed handshake
   * lands simply omit it (and pick it up once it's populated).
   */
  practiceId?(): string | null
}

/**
 * Which TelemetryDeck app this build reports to. Drives app-ID resolution so
 * the patient (iOS/Android/web-dev) and therapist (web) dashboards stay fully
 * separate. Defaults to `'patient'` so the existing native call site is
 * unchanged.
 */
export type TelemetryApp = 'patient' | 'therapist'

export interface InitTelemetryOptions {
  /** Selects the TelemetryDeck app / dashboard. Defaults to `'patient'`. */
  app?: TelemetryApp
  defaults: TelemetryDefaultsProvider
  /**
   * Override the env-derived app ID. Primarily for tests. `null` forces
   * the wrapper into no-op mode regardless of env.
   */
  appIdOverride?: string | null
}

let client: TelemetryDeck | null = null
let sessionId = ''
let userId: string | null = null
let providers: TelemetryDefaultsProvider | null = null

const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev'

/**
 * `YYMMDDHHT`, e.g. `261013111`. The same value the binary carries as
 * CURRENT_PROJECT_VERSION / versionCode, and the same one Sentry receives as
 * `dist` — so "which build is this?" is one number across the error tracker,
 * both stores and this dashboard.
 *
 * Always defined: vite.config.ts injects it on every build. The fallback is for
 * the Vitest environment, which resolves the config but not the `define`.
 */
const APP_BUILD =
  (import.meta.env.VITE_APP_BUILD as string | undefined) ?? 'dev'

function platformLabel(): 'ios' | 'android' | 'web' {
  if (isIOS) return 'ios'
  if (isAndroid) return 'android'
  return 'web'
}

/**
 * Resolve the TelemetryDeck app ID for the current runtime platform. Each
 * platform points at its own TelemetryDeck app so iOS / Android funnels
 * don't share a single dashboard — Apple and Google revenue cuts are
 * different, conversion rates differ, store-side promotions differ, etc.
 * Returning `undefined` puts the wrapper into no-op mode for that
 * platform (see {@link initTelemetry}).
 */
function platformAppId(): string | undefined {
  if (isIOS) {
    return import.meta.env.VITE_TELEMETRYDECK_APP_ID_IOS as string | undefined
  }
  if (isAndroid) {
    return import.meta.env.VITE_TELEMETRYDECK_APP_ID_ANDROID as
      | string
      | undefined
  }
  return import.meta.env.VITE_TELEMETRYDECK_APP_ID_WEB as string | undefined
}

/**
 * The therapist web app reports to its own TelemetryDeck app, fully separate
 * from the patient (mobile/web-dev) dashboards — the two data streams must
 * never mix. The native patient bundle never reads this var (it always
 * resolves via {@link platformAppId}), and `.env.capacitor` deliberately
 * leaves it undefined.
 */
function therapistAppId(): string | undefined {
  return import.meta.env.VITE_TELEMETRYDECK_APP_ID_THERAPIST as
    | string
    | undefined
}

/**
 * Resolve the env app ID for a given build role. Explicit (not platform-
 * inferred) so a build can never accidentally cross dashboards: patient →
 * per-platform id, therapist → the dedicated therapist id. Exported for unit
 * tests.
 */
export function resolveAppId(app: TelemetryApp): string | undefined {
  return app === 'therapist' ? therapistAppId() : platformAppId()
}

/**
 * Idempotent. Safe to call before any `setTelemetryUser()` — the SDK starts
 * with an anonymous user that will be replaced as soon as a patient id is
 * available. Calling twice is a no-op.
 */
export function initTelemetry(opts: InitTelemetryOptions): void {
  if (client !== null) return
  providers = opts.defaults
  if (sessionId === '') sessionId = generateUuid()

  const envAppId = resolveAppId(opts.app ?? 'patient')
  const resolved =
    opts.appIdOverride === undefined ? envAppId : (opts.appIdOverride ?? undefined)

  if (resolved === undefined || resolved === '') {
    // No-op mode: providers stay set so the unit test can drive the
    // payload-merge path via `_buildPayloadForTests` without a live SDK.
    return
  }

  // The TelemetryDeck SDK SHA-256s clientUser via SubtleCrypto on every
  // signal. SubtleCrypto is gated to secure contexts — production
  // Capacitor (`capacitor://localhost`) qualifies, but Vite live-reload
  // loads from `http://<lan-ip>:5174` which doesn't, leaving
  // `crypto.subtle` undefined. Without it every signal() throws with
  // `undefined is not an object (evaluating 'subtleCrypto.digest')`,
  // which floods the iOS console. Drop into no-op mode instead, log
  // once so the dev knows why nothing's reaching the dashboard, and
  // ship without spam.
  if (
    typeof globalThis.crypto === 'undefined' ||
    typeof globalThis.crypto.subtle === 'undefined'
  ) {
    console.info(
      '[telemetry] disabled: SubtleCrypto unavailable (insecure context — likely Capacitor live-reload over HTTP). Production builds load from capacitor://localhost and will emit normally.',
    )
    return
  }

  client = new TelemetryDeck({
    appID: resolved,
    // TelemetryDeck requires a clientUser at construction time. Empty until
    // bootstrap; `setTelemetryUser` updates the instance in place once the
    // patient token is minted.
    clientUser: userId ?? 'anonymous',
    sessionID: sessionId,
    testMode: import.meta.env.DEV,
  })
}

/**
 * Set the anonymous user identifier. TelemetryDeck SHA-256s the value
 * before transmission, so passing the opaque patient_id is safe.
 */
export function setTelemetryUser(id: string): void {
  userId = id
  if (client !== null) {
    client.clientUser = id
  }
}

/**
 * Fire a signal. Silent no-op when the SDK is unconfigured. Errors are
 * swallowed (with a console.warn) — analytics must never break the user-
 * facing flow.
 */
export function signal(type: string, props?: Record<string, unknown>): void {
  if (client === null) return
  const payload = buildPayload(props)
  client.signal(type, payload).catch((e: unknown) => {
    // Error / Response instances stringify as `{}` via the WebView's default
    // console serializer because their fields aren't enumerable. Unpack the
    // bits a developer needs (HTTP status for Response, name+message for
    // Error) so the iOS console actually tells you why the signal failed.
    console.warn('[telemetry] signal failed', type, describeSignalError(e))
  })
}

function describeSignalError(e: unknown): string {
  if (e instanceof Response) return `Response HTTP ${e.status} ${e.statusText}`
  if (e instanceof Error) {
    return `${e.name || 'Error'}: ${e.message || '(no message)'}`
  }
  if (typeof e === 'string') return e
  try {
    return JSON.stringify(e)
  } catch {
    return String(e)
  }
}

function buildPayload(
  props: Record<string, unknown> | undefined,
): Record<string, string> {
  const merged: Record<string, unknown> = {
    platform: platformLabel(),
    app_version: APP_VERSION,
    // YYMMDDHHT. Rides every signal so a funnel can be sliced by exact build,
    // not just by marketing version — a regression introduced in one respin is
    // otherwise invisible inside "1.2.0".
    build_number: APP_BUILD,
    session_id: sessionId,
    // Consumer vs. the €99 institution-purchased VPP bundle. On every signal
    // because the two editions share one dashboard but almost no funnel:
    // without it, VPP installs would silently drag every trial/purchase
    // conversion rate towards zero.
    edition,
  }
  if (providers !== null) {
    // `locale` is supplied by every build; the rest are role-specific and only
    // merged when the provider actually exposes them (entitlement/trial are
    // patient-only; role/practice_id are therapist-relevant).
    merged.locale = providers.locale()
    if (providers.role) merged.role = providers.role()
    if (providers.entitlementStatus) {
      merged.entitlement_status = providers.entitlementStatus()
    }
    if (providers.trialDaysRemaining) {
      const trial = providers.trialDaysRemaining()
      if (trial !== null) merged.trial_days_remaining = trial
    }
    if (providers.practiceId) {
      const practiceId = providers.practiceId()
      if (practiceId) merged.practice_id = practiceId
    }
  }
  Object.assign(merged, props ?? {})

  // TelemetryDeck payloads serialize as flat key/value maps; cast every
  // value to string and drop null/undefined entries.
  const flat: Record<string, string> = {}
  for (const [k, v] of Object.entries(merged)) {
    if (v === null || v === undefined) continue
    flat[k] = typeof v === 'string' ? v : String(v)
  }
  return flat
}

/** Test-only — reset module state so each unit test starts fresh. */
export function _resetTelemetryForTests(): void {
  client = null
  sessionId = ''
  userId = null
  providers = null
}

/** Test-only — peek at whether the SDK was initialized. */
export function _isTelemetryActive(): boolean {
  return client !== null
}

/** Test-only — build the default-merged payload without sending. */
export function _buildPayloadForTests(
  props?: Record<string, unknown>,
): Record<string, string> {
  return buildPayload(props)
}
